import { Auth0Authorization } from "./auth0/auth0-authorization";
import { KeycloakAuthorization } from "./keycloak/keycloak-authorization";
import { Authorization } from "./Authorization";
import { clientProvider } from "./keycloak/keycloak-client";
import { CockpitUser, MekUser } from "./api";
import { pushUserInfoTracking, pushUserInfoTrackingControl } from "./user-tracking";
import { isHostControl } from "./urlUtils";
import { loadStoredOrFetchConfiguration } from "@newsenec/configuration-provider";

const keycloakKey = "keycloak";
const auth0Key = "auth0";

export class AuthService implements Authorization {
    private authorization?: Authorization;
    private readonly initialization: Promise<void>;

    constructor() {
        this.initialization = this.initializeAuthorization();
    }

    private async initializeAuthorization(): Promise<void> {
        const authStrategy = localStorage.getItem("auth-strategy");
        const keycloakDefault = await loadStoredOrFetchConfiguration()
            .then((config) => config.keycloakAsDefault ?? false)
            .catch(() => false);

        if (isHostControl() || authStrategy === keycloakKey || (keycloakDefault && authStrategy !== auth0Key)) {
            this.authorization = new KeycloakAuthorization(clientProvider);
        } else {
            this.authorization = new Auth0Authorization();
        }
    }

    private async ensureInitialized(): Promise<void> {
        await this.initialization;
    }

    async getAccessToken(): Promise<string> {
        await this.ensureInitialized();
        return this.authorization!.getAccessToken();
    }

    async getCockpitUser(): Promise<CockpitUser> {
        await this.ensureInitialized();
        return this.authorization!.getCockpitUser();
    }

    async getUser(): Promise<MekUser | undefined> {
        await this.ensureInitialized();
        return this.authorization!.getUser();
    }

    async loginSilently(): Promise<MekUser | undefined> {
        await this.ensureInitialized();
        const user = await this.authorization!.loginSilently();
        this.trackLoggingSilently(user);
        return user;
    }

    async loginWithRedirect(): Promise<void> {
        await this.ensureInitialized();
        return this.authorization!.loginWithRedirect();
    }

    async registerWithRedirect(): Promise<void> {
        // register with redirect is only supported for Auth0
        this.authorization = new Auth0Authorization();
        await this.ensureInitialized();
        return this.authorization!.registerWithRedirect();
    }

    async logout(url?: string): Promise<void> {
        await this.ensureInitialized();
        return this.authorization!.logout(url);
    }

    async refreshToken(): Promise<string> {
        await this.ensureInitialized();
        return this.authorization!.refreshToken();
    }

    private async trackLoggingSilently(user: MekUser | undefined) {
        await this.ensureInitialized();
        if (!isHostControl() && user?.devNumber) {
            this.authorization!.getCockpitUser()
                .then(pushUserInfoTracking)
                .catch(() => {});
        } else if (isHostControl() && user?.keycloakRoles && user.keycloakRoles.length > 0) {
            pushUserInfoTrackingControl(user);
        }
    }
}
